import { Controller } from '@hotwired/stimulus'

/**
 * Number Selector Controller
 *
 * Manages a multi-step phone number selection interface with the following flow:
 * 1. User selects number type (Toll-free or Local)
 * 2. User selects country (different options shown based on type)
 * 3. System loads available prefixes/area codes for that country+type
 * 4. User can either:
 *    - Select a specific prefix to see available numbers
 *    - Search for numbers containing specific digits
 * 5. System displays available numbers for purchase
 *
 * Uses turbo frames for dynamic loading of:
 * - prefix_selector: Area codes/prefixes based on country+type
 * - available_numbers: Actual phone numbers available for purchase
 *
 * See implementations:
 * app/views/number_selector/_number_selector.html.erb
 *
 * available_numbers_controller.rb
 * app/views/available_numbers/_number.html.erb
 *
 */
export default class extends Controller {
    static targets = [
        'typeRadio',
        'tollFreeRadio',
        'tollFreeCountryWrapper',
        'localCountryWrapper',
        'tollFreeCountrySelect',
        'localCountrySelect',
        'areaCodeId',
        'availableNumbersFrame',
        'prefixSelectorFrame',
    ]
    static values = {
        areaCodesUrl: String,
        availableNumbersUrl: String,
        selectedAreaCodeId: String, // Previously selected area code id when form fails
    }

    typeChanged() {
        const selectedType = this.getSelectedType()

        // Show/hide country selects based on type
        this.toggleCountrySelects(selectedType)

        // Clear dependent sections
        this.clearFrames(['prefix_selector', 'available_numbers'])

        // Trigger country change if there's a selected country
        const selectedCountry = this.getSelectedCountry()
        if (selectedCountry) {
            this.countryChanged()
        }
    }

    countryChanged() {
        const country = this.getSelectedCountry()
        const type = this.getSelectedType()

        // Update prefix options
        this.updatePrefixes(country, type)

        // Clear available numbers
        this.clearFrames(['available_numbers'])
    }

    prefixChanged(event) {
        const prefixId = event.target.value

        if (this.hasAreaCodeIdTarget) {
            // NumberPool takes an area_code_id
            this.areaCodeIdTarget.value = prefixId
        }

        if (prefixId) {
            this.loadAvailableNumbers({ area_code_id: prefixId })
        } else {
            this.clearFrames(['available_numbers'])
        }
    }

    searchNumbers(event) {
        const contains = event.target.value

        if (contains.length > 1) {
            // Debounce search
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.loadAvailableNumbers({ contains })
            }, 500)
        } else {
            this.clearFrames(['available_numbers'])
        }
    }

    refreshNumbers() {
        this.availableNumbersFrameTarget.reload()
    }

    toggleCountrySelects(type) {
        if (type === 'Toll-free') {
            // Show toll-free, hide local
            this.tollFreeCountryWrapperTarget.classList.remove('d-none')
            this.localCountryWrapperTarget.classList.add('d-none')
            this.toggleInputsInWrapper(this.tollFreeCountryWrapperTarget, false)
            this.toggleInputsInWrapper(this.localCountryWrapperTarget, true)
        } else {
            // Show local, hide toll-free
            this.tollFreeCountryWrapperTarget.classList.add('d-none')
            this.localCountryWrapperTarget.classList.remove('d-none')
            this.toggleInputsInWrapper(this.tollFreeCountryWrapperTarget, true)
            this.toggleInputsInWrapper(this.localCountryWrapperTarget, false)
        }
    }

    toggleInputsInWrapper(wrapper, disabled) {
        const inputs = wrapper.querySelectorAll('input, select, textarea')
        inputs.forEach(input => (input.disabled = disabled))
    }

    updatePrefixes(country, type) {
        const url = `${this.areaCodesUrlValue}?country=${country}&type=${type}&area_code_id=${this.selectedAreaCodeIdValue}`
        this.prefixSelectorFrameTarget.src = url
    }

    loadAvailableNumbers(searchParams = {}) {
        const params = {
            country: this.getSelectedCountry(),
            type: this.getSelectedType(),
            ...searchParams,
        }

        const url = `${this.availableNumbersUrlValue}?${new URLSearchParams(params)}`
        this.availableNumbersFrameTarget.src = url
    }

    getSelectedCountry() {
        // Get the value from the visible/enabled country select
        if (!this.tollFreeCountrySelectTarget.disabled) {
            return this.tollFreeCountrySelectTarget.value
        }
        if (!this.localCountrySelectTarget.disabled) {
            return this.localCountrySelectTarget.value
        }

        return 'US' // fallback
    }

    getSelectedType() {
        if (this.hasTollFreeRadioTarget) {
            // NumberPool uses a toll_free boolean
            const checkedRadio = this.tollFreeRadioTargets.find(radio => radio.checked)
            return checkedRadio?.value === 'true' ? 'Toll-free' : 'Local'
        }
        // Number uses a number_type enum
        return this.typeRadioTargets.find(radio => radio.checked)?.value || 'Toll-free'
    }

    clearFrames(frameIds) {
        frameIds.forEach((id) => {
            const frame = document.getElementById(id)
            if (frame) frame.innerHTML = ''
        })
    }
}
